import { TooltipProvider } from "@thilke/ui/components/tooltip";
import registry from "@thilke/ui/registry";
import { useEffect, useMemo, useState } from "react";
import { AppContentHeader } from "./components/AppContentHeader";
import { AppSidebar } from "./components/AppSidebar";
import { ComponentsPage } from "./pages/ComponentsPage";
import type { RegistryTheme } from "./theme-options";

type RegistryFile = { path: string; type?: string };
type RegistryItem = {
  name: string;
  type: string;
  title?: string;
  description?: string;
  dependencies?: string[];
  registryDependencies?: string[];
  categories?: string[];
  files?: RegistryFile[];
};
type Registry = { name: string; homepage?: string; items: RegistryItem[] };

const typedRegistry = registry as Registry;

function getOrigin() {
  if (typeof window === "undefined") return "https://ui.thilke.com";
  return window.location.origin;
}

export function App() {
  const [query, setQuery] = useState("");
  const [theme, setTheme] = useState<RegistryTheme>("foundry-dark");
  const origin = getOrigin();

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const items = useMemo(() => typedRegistry.items, []);
  const filteredItems = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) =>
      [
        item.name,
        item.title,
        item.description,
        ...(item.dependencies ?? []),
        ...(item.registryDependencies ?? []),
        ...(item.files ?? []).map((file) => file.path),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [items, query]);

  return (
    <TooltipProvider>
      <div
        data-theme={theme}
        className="min-h-dvh bg-background text-foreground"
      >
        <div className="flex min-h-dvh w-full">
          <AppSidebar
            origin={origin}
            registryName={typedRegistry.name}
            items={items}
            filteredItems={filteredItems}
            query={query}
            onQueryChange={setQuery}
            theme={theme}
            onThemeChange={setTheme}
          />
          <main className="min-w-0 flex-1">
            <AppContentHeader
              origin={origin}
              itemCount={items.length}
              visibleCount={filteredItems.length}
              theme={theme}
              onThemeChange={setTheme}
            />
            <div className="mx-auto grid max-w-6xl gap-6 p-4 md:p-6">
              <ComponentsPage
                origin={origin}
                items={filteredItems}
                registryName={typedRegistry.name}
              />
            </div>
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
